import { redirect, type RouteObject } from 'react-router';

import { SaasEscrowOnboardingRoutesEnum } from 'src/pages/SaasEscrowOnboarding';
import { EscrowOnboardingNewRoutesEnum } from 'src/pages/EscrowOnboardingAccess/routes.enum';
import { SaasEscrowRoutesEnum, SaasEscrowSettingsRoutesEnum } from 'src/pages/SaasEscrow';
import { SaasEscrowRequestChangesRoutesEnum } from 'src/pages/SaasEscrowRequestChanges';

export const saasEscrowsOnboardingRoutes: RouteObject = {
  path: 'saas-escrows/onboarding',
  children: [
    {
      path: 'new/:appId',
      lazy: async () => {
        const { default: Component } = await import('src/pages/SaasEscrowOnboarding/New');

        return { Component };
      },
    },
    {
      path: `new/:appId/${EscrowOnboardingNewRoutesEnum.Access}`,
      lazy: async () => {
        const { default: Component } = await import('src/pages/EscrowOnboardingAccess');

        return { Component };
      },
    },
    {
      path: ':id/*',
      lazy: async () => {
        const { Layout } = await import('src/pages/SaasEscrowOnboarding/ui');

        return { Component: Layout };
      },
      children: [
        {
          path: SaasEscrowOnboardingRoutesEnum.Members,
          lazy: async () => {
            const { default: Component } = await import('src/pages/SaasEscrowOnboarding/Members');

            return { Component };
          },
        },
        {
          path: SaasEscrowOnboardingRoutesEnum.Depositor,
          lazy: async () => {
            const { default: Component } = await import('src/pages/SaasEscrowOnboarding/Depositor');

            return { Component };
          },
        },
        {
          path: SaasEscrowOnboardingRoutesEnum.Beneficiary,
          lazy: async () => {
            const { default: Component } = await import('src/pages/SaasEscrowOnboarding/Beneficiary');

            return { Component };
          },
        },
        {
          path: SaasEscrowOnboardingRoutesEnum.NotificationSettings,
          lazy: async () => {
            const { default: Component } = await import('src/pages/SaasEscrowOnboarding/NotificationSettings');

            return { Component };
          },
        },
        {
          path: SaasEscrowOnboardingRoutesEnum.ActivationNotifications,
          lazy: async () => {
            const { default: Component } = await import('src/pages/SaasEscrowOnboarding/ActivationNotifications');

            return { Component };
          },
        },
        {
          path: SaasEscrowOnboardingRoutesEnum.StorageSettings,
          lazy: async () => {
            const { default: Component } = await import('src/pages/SaasEscrowOnboarding/StorageSettings');

            return { Component };
          },
        },
        {
          path: SaasEscrowOnboardingRoutesEnum.AssetVisibility,
          lazy: async () => {
            const { default: Component } = await import('src/pages/SaasEscrowOnboarding/AssetVisibility');

            return { Component };
          },
        },
        {
          path: SaasEscrowOnboardingRoutesEnum.AgreementSetup,
          lazy: async () => {
            const { default: Component } = await import('src/pages/SaasEscrowOnboarding/AgreementSetup');

            return { Component };
          },
        },
        {
          path: SaasEscrowOnboardingRoutesEnum.AgreementDetails,
          lazy: async () => {
            const { default: Component } = await import('src/pages/SaasEscrowOnboarding/AgreementDetails');

            return { Component };
          },
        },
        {
          path: SaasEscrowOnboardingRoutesEnum.Summary,
          lazy: async () => {
            const { default: Component } = await import('src/pages/SaasEscrowOnboarding/Summary');

            return { Component };
          },
        },
      ],
    },
  ],
};

export const saasEscrowRoutes: RouteObject = {
  path: 'saas-escrows/:id/*',
  lazy: async () => {
    const { SoftwareEscrowLayout } = await import('src/pages/SaasEscrow/ui');

    return { Component: SoftwareEscrowLayout };
  },
  loader: ({ params }) => {
    const { id, '*': path = '' } = params;

    if (!path) return redirect(`/saas-escrows/${id}/${SaasEscrowRoutesEnum.Overview}`);

    if (
      !Object.values(SaasEscrowRoutesEnum).includes(path as SaasEscrowRoutesEnum) &&
      !Object.values(SaasEscrowSettingsRoutesEnum).includes(path as SaasEscrowSettingsRoutesEnum)
    )
      return redirect('/404');

    return null;
  },
  children: [
    {
      path: SaasEscrowRoutesEnum.Overview,
      lazy: async () => {
        const { default: Component } = await import('src/pages/SaasEscrow/Overview');

        return { Component };
      },
    },
    {
      path: SaasEscrowRoutesEnum.Agreement,
      lazy: async () => {
        const { default: Component } = await import('src/pages/SaasEscrow/Agreement');

        return { Component };
      },
    },
    {
      path: SaasEscrowRoutesEnum.Deposits,
      lazy: async () => {
        const { default: Component } = await import('src/pages/SaasEscrow/Deposits');

        return { Component };
      },
    },
    {
      path: SaasEscrowRoutesEnum.Reports,
      lazy: async () => {
        const { default: Component } = await import('src/pages/SaasEscrow/Reports');

        return { Component };
      },
    },
    {
      path: SaasEscrowRoutesEnum.Members,
      lazy: async () => {
        const { default: Component } = await import('src/pages/SaasEscrow/Members');

        return { Component };
      },
    },
    {
      path: `${SaasEscrowRoutesEnum.Settings}/*`,
      lazy: async () => {
        const { SettingsLayout: Component } = await import('src/pages/SaasEscrow/Settings');

        return { Component };
      },
      loader: ({ params }) => {
        const { id, '*': path = '' } = params;

        const defaultLink = `/saas-escrows/${id}/${SaasEscrowRoutesEnum.Settings}/${SaasEscrowSettingsRoutesEnum.Depositor}`;
        const isValidPath = Object.values(SaasEscrowSettingsRoutesEnum).includes(path as SaasEscrowSettingsRoutesEnum);

        if (!path || !isValidPath) return redirect(defaultLink);

        return null;
      },
      children: [
        {
          path: SaasEscrowSettingsRoutesEnum.Agreement,
          lazy: async () => {
            const { SettingsAgreement: Component } = await import('src/pages/SaasEscrow/Settings');

            return { Component };
          },
        },
        {
          path: SaasEscrowSettingsRoutesEnum.Beneficiary,
          lazy: async () => {
            const { SettingsBeneficiary: Component } = await import('src/pages/SaasEscrow/Settings');

            return { Component };
          },
        },
        {
          path: SaasEscrowSettingsRoutesEnum.Depositor,
          lazy: async () => {
            const { SettingsDepositor: Component } = await import('src/pages/SaasEscrow/Settings');

            return { Component };
          },
        },
        {
          path: SaasEscrowSettingsRoutesEnum.Technical,
          lazy: async () => {
            const { SettingsTechnical: Component } = await import('src/pages/SaasEscrow/Settings');

            return { Component };
          },
        },
      ],
    },
  ],
};

export const saasEscrowsRequestChangesRoutes: RouteObject = {
  path: 'saas-escrows/:id/request-changes/*',
  lazy: async () => {
    const { SaasEscrowRequestChangesLayout } = await import('src/pages/SaasEscrowRequestChanges');

    return { Component: SaasEscrowRequestChangesLayout };
  },
  children: [
    {
      path: SaasEscrowRequestChangesRoutesEnum.Depositor,
      lazy: async () => {
        const { SaasEscrowRequestChangesDepositor: Component } = await import('src/pages/SaasEscrowRequestChanges');

        return { Component };
      },
    },
    {
      path: SaasEscrowRequestChangesRoutesEnum.Beneficiary,
      lazy: async () => {
        const { SaasEscrowRequestChangesBeneficiary: Component } = await import('src/pages/SaasEscrowRequestChanges');

        return { Component };
      },
    },
    {
      path: SaasEscrowRequestChangesRoutesEnum.Technical,
      lazy: async () => {
        const { SaasEscrowRequestChangesTechnical: Component } = await import('src/pages/SaasEscrowRequestChanges');

        return { Component };
      },
    },
    {
      path: SaasEscrowRequestChangesRoutesEnum.Agreement,
      lazy: async () => {
        const { SaasEscrowRequestChangesAgreement: Component } = await import('src/pages/SaasEscrowRequestChanges');

        return { Component };
      },
    },
    {
      path: SaasEscrowRequestChangesRoutesEnum.Deposits,
      lazy: async () => {
        const { SaasEscrowRequestChangesDeposits: Component } = await import('src/pages/SaasEscrowRequestChanges');

        return { Component };
      },
    },
  ],
};
